import { useTranslation } from 'react-i18next'
import { ArrowLeft, Coins } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Tournament } from '@brochure/shared'
import { LocalizedLink } from '@/components/LocalizedLink'
import { formatTournamentDate } from '@/lib/zambepong-format'
import { StatusStamp } from '@/components/zambepong/StatusStamp'

type TournamentHeaderProps = {
  tournament: Tournament
  isLive: boolean
  wallet?: { name: string; balance: number }
}

/**
 * Intestazione della pagina torneo: ritorno a tutti i tornei, data, nome, stato, badge LIVE (solo a torneo in corso
 * e socket aperto) e il portafoglio dell'ospite come un piccolo biglietto. Titolo contenuto: i nomi dei tornei possono
 * essere lunghi e su un telefono non devono prendersi mezzo schermo.
 */
export function TournamentHeader({ tournament, isLive, wallet }: TournamentHeaderProps) {
  const { t, i18n } = useTranslation()
  return (
    <header>
      <div className="flex items-center justify-between gap-3">
        <LocalizedLink
          to="zambePong"
          className="-ml-2 inline-flex min-h-11 items-center gap-1.5 rounded-full px-2 font-body text-sm font-semibold text-ink-soft transition-colors hover:bg-ink/5 hover:text-ink"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          {t('zambePong.allTournaments')}
        </LocalizedLink>
        {/* ZambeBet ha senso solo con le partite in calendario: da torneo avviato in poi. */}
        {(tournament.status === 'active' || tournament.status === 'completed') && (
          <LocalizedLink
            to="zambePongZambeBet"
            params={{ id: tournament.id }}
            data-testid="zambebet-link"
            className="inline-flex min-h-11 items-center gap-2 rounded-full bg-coral px-5 font-display text-base font-semibold text-white shadow-card transition-[background-color,transform] duration-200 ease-out-expo hover:-translate-y-0.5 hover:bg-coral-ink active:scale-95"
          >
            <Coins className="size-5 text-sun" aria-hidden="true" />
            {t('zambebet.title')}
          </LocalizedLink>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
        <div className="min-w-0">
          <p className="font-body text-sm font-semibold text-ink-soft">{formatTournamentDate(tournament.date, i18n.language)}</p>
          <h1 className="hero-title mt-1 text-[clamp(2.25rem,1.4rem+4.5vw,5rem)] text-ink">{tournament.name}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <StatusStamp status={tournament.status} />
            {isLive && tournament.status === 'active' && (
              <span data-testid="live" className="inline-flex items-center gap-2 font-body text-sm font-semibold tracking-[0.02em] text-coral-ink">
                <span className="size-2 rounded-full bg-coral motion-safe:animate-pulse" aria-hidden="true" />
                {t('zambePong.live')}
              </span>
            )}
          </div>
        </div>

        {wallet && <WalletCard name={wallet.name} balance={wallet.balance} />}
      </div>
    </header>
  )
}

/** Portafoglio dell'ospite: la moneta d'oro, il nome e il saldo, su un cartoncino con filetto d'oro. */
export function WalletCard({ name, balance, className }: { name: string; balance: number; className?: string }) {
  const { t } = useTranslation()
  return (
    <div
      data-testid="wallet"
      className={cn('flex items-center gap-3 rounded-full border border-line bg-paper py-1.5 pr-5 pl-1.5 font-body text-ink shadow-card', className)}
    >
      <span className="grid size-10 place-items-center rounded-full bg-sun text-ink">
        <Coins className="size-5" aria-hidden="true" />
      </span>
      <span className="flex flex-col justify-center">
        <span className="text-xs font-semibold text-ink-soft">{name}</span>
        <span className="text-base font-semibold tabular-nums">{t('zambePong.wallet.coins', { count: balance })}</span>
      </span>
    </div>
  )
}
